import { useCallback, useMemo, useState } from 'react';
import { BiPlusCircle } from 'react-icons/bi';
import { IconButton } from '@ds';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { RouteModalTypes } from '@lp/features/RoutePlanner/types';
import { useRouteModalActions } from '@lp/features/RoutePlanner/hooks/useRouteModals';
import { useResourcesList } from '@lp/features/RoutePlanner/hooks/useResourcesList';
import { useAuthContext } from '@lp/core/auth';
import { Case, CaseStatus, RoutePlanStatus } from '@/api/apis/case';
import { RouteBuilderPermissions } from '@/permissions';
import { LogisticsSlider } from './LogisticsSlider';
import { LogisticsResourcesList } from './LogisticsResourcesList';
import { isAllowedStatus } from '@/utils/helpers';

const DEFAULT_ITEMS_PER_PAGE = 3;

export const LogisticsResources = () => {
  const { checkPermission } = useAuthContext();
  const { onOpenModal } = useRouteModalActions();
  const { status, routePlanningStatus, donorHospitalDetails: donorHospital } = useCaseDetailsWithReviews() as Case;
  const [page, setPage] = useState(0);

  const mappedResources = useResourcesList();

  const resourcesToRender = useMemo(() => {
    const startIndex = page * DEFAULT_ITEMS_PER_PAGE;
    const endIndex = startIndex + DEFAULT_ITEMS_PER_PAGE;
    return mappedResources.slice(startIndex, endIndex);
  }, [mappedResources, page]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage],
  );

  const openSecondaryResourceModal = () => {
    onOpenModal({
      type: RouteModalTypes.SecondaryResource,
      context: null,
    });
  };

  const canAssignResources = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.AssignResource);
    const isCorrectStatus = routePlanningStatus === RoutePlanStatus.RoutePlanningStart;
    const isActiveCaseStatus = isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });

    return hasPermission && isCorrectStatus && isActiveCaseStatus;
  }, [checkPermission, routePlanningStatus, status]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row justify-between items-center">
        <div className="flex gap-2 items-center">
          <span className="text-l-sb">Resources</span>
          <LogisticsSlider
            totalCount={mappedResources.length}
            page={page}
            itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
        </div>
        {canAssignResources && (
          <IconButton size="sm" variant="light" startContent={<BiPlusCircle />} onPress={openSecondaryResourceModal} />
        )}
      </div>
      <LogisticsResourcesList resources={resourcesToRender} donorHospital={donorHospital} />
    </div>
  );
};
